import type { CSSProperties } from 'react';

import {
  WeekMenuPrintData,
  WeekMenuPrintDay,
  WeekMenuPrintShiftInfo,
} from '@/components/restaurant/menus/week-menu-print-data';

interface WeekMenuPrintProps {
  restaurantName: string;
  weekLabel: string;
  data: WeekMenuPrintData;
}

export const WeekMenuPrint = ({
  restaurantName,
  weekLabel,
  data,
}: WeekMenuPrintProps) => {
  const { days, shifts } = data;
  const dense = days.length > 5;
  const bodyPt = dense ? 9.5 : 10.5;
  const headingPt = dense ? 11 : 12.5;

  const cellBorder = '1px solid #444';

  const rowPadding = dense ? '2pt 4pt' : '3pt 5pt';

  const dayCell: CSSProperties = {
    border: cellBorder,
    padding: 0,
    verticalAlign: 'top',
    textAlign: 'left',
    fontSize: `${bodyPt}pt`,
    lineHeight: 1.25,
    width: `${100 / days.length}%`,
  };

  const renderShiftBlock = (
    shiftInfo: WeekMenuPrintShiftInfo,
    index: number,
  ) => (
    <table
      key={shiftInfo.shift}
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
        marginTop: index === 0 ? 0 : '14pt',
        breakInside: index === 0 ? 'auto' : 'avoid',
        pageBreakInside: index === 0 ? 'auto' : 'avoid',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
    >
      <thead>
        {shiftInfo.label && (
          <tr>
            <th
              colSpan={days.length}
              style={{
                border: cellBorder,
                background: '#222',
                color: '#fff',
                padding: dense ? '3pt 6pt' : '4pt 8pt',
                textAlign: 'center',
                fontSize: `${headingPt}pt`,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              {shiftInfo.label}
            </th>
          </tr>
        )}
        <tr>
          {days.map((day) => (
            <th
              key={day.date}
              style={{
                ...dayCell,
                textAlign: 'center',
                background: '#e8e8e8',
                fontSize: `${headingPt}pt`,
                fontWeight: 700,
                padding: dense ? '4pt' : '5pt',
                lineHeight: 1.2,
              }}
            >
              {day.weekday}
              <div style={{ fontSize: `${bodyPt}pt`, fontWeight: 500 }}>
                {day.dateLabel}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          {days.map((day) => (
            <td key={day.date} style={dayCell}>
              {renderDayShift(day, shiftInfo.shift)}
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  );

  const renderDayShift = (day: WeekMenuPrintDay, shift: string) => {
    const dayShift = day.shifts.find((entry) => entry.shift === shift);
    if (!dayShift) {
      return (
        <div
          style={{ textAlign: 'center', color: '#888', padding: rowPadding }}
        >
          —
        </div>
      );
    }

    return dayShift.menus.map((menu, menuIndex) => (
      <div key={menu.id}>
        <div
          style={{
            fontWeight: 700,
            background: '#e0e0e0',
            borderTop: menuIndex === 0 ? 'none' : cellBorder,
            borderBottom: cellBorder,
            padding: rowPadding,
            // Keep a menu heading with at least its first items across a page break.
            breakAfter: 'avoid',
            pageBreakAfter: 'avoid',
          }}
        >
          {menu.name}
        </div>
        {menu.foods.map((food, foodIndex) => (
          <div
            key={foodIndex}
            style={{
              padding: rowPadding,
              borderBottom:
                foodIndex === menu.foods.length - 1 ? 'none' : '1px solid #bbb',
              breakInside: 'avoid',
              pageBreakInside: 'avoid',
            }}
          >
            {food}
          </div>
        ))}
      </div>
    ));
  };

  return (
    <div style={{ color: '#000', fontFamily: 'sans-serif' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: '16pt',
          marginBottom: dense ? '5pt' : '7pt',
        }}
      >
        <h1
          style={{
            fontSize: dense ? '14pt' : '16pt',
            fontWeight: 700,
            margin: 0,
          }}
        >
          {restaurantName}
        </h1>
        <span style={{ fontSize: `${headingPt}pt`, fontWeight: 600 }}>
          {weekLabel}
        </span>
      </div>
      {shifts.map(renderShiftBlock)}
    </div>
  );
};
